import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Button,
  Drawer,
  IconButton,
  Divider,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as ShippingIcon,
  Download as DownloadIcon,
  AssessmentOutlined as AssessmentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { activityTypeLabels } from '../theme';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../components/layout/PageHeader';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { reportApi, customerApi } from '../api/client';
import { useAuthStore } from '../store/authStore';

const CHART_COLORS = [
  '#208040', '#60A080', '#C0E0C0', '#3E7A5A', '#16602F',
  '#E8A33D', '#3E9AB5', '#C74A3E', '#9CA9A1', '#2F5C42',
  '#7FB89A', '#A8D2B0', '#4A9E6E', '#1F4D2F', '#6FAF8A',
];

const statusLabels: Record<string, string> = {
  NO_HISTORY: 'Ingen historik',
  LEAD: 'Lead',
  CONTACTED: 'Kontaktet',
  OFFER_SENT: 'Tilbud sendt',
  ACTIVE: 'Aktiv',
  INACTIVE: 'Inaktiv',
  LOST: 'Tabt',
};

const statusColors: Record<string, string> = {
  NO_HISTORY: '#bdbdbd',
  LEAD: '#9e9e9e',
  CONTACTED: '#2196f3',
  OFFER_SENT: '#ff9800',
  ACTIVE: '#4caf50',
  INACTIVE: '#9e9e9e',
  LOST: '#f44336',
};

const periodLabels: Record<string, string> = {
  week: 'denne uge',
  month: 'denne måned',
  quarter: 'dette kvartal',
  year: 'dette år',
  all: 'alle perioder',
};

const performanceUserName = (performance: any, userId: string | null): string => {
  const u = performance?.userPerformance?.find((x: any) => x.userId === userId);
  return u ? `${u.firstName} ${u.lastName}` : '';
};

export const Reports: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN' || user?.isSuperAdmin;
  const [period, setPeriod] = useState('month');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const theme = useTheme();
  const fullScreenDrawer = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: userDetail, isLoading: userDetailLoading } = useQuery({
    queryKey: ['reports', 'performance', 'detail', selectedUserId, period],
    queryFn: async () => {
      const res = await reportApi.getPerformanceDetail(selectedUserId!, { period });
      return res.data.data;
    },
    enabled: isAdmin && !!selectedUserId,
  });

  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: async () => {
      const res = await reportApi.getDashboard();
      return res.data.data;
    },
  });

  const { data: pipeline, isLoading: pipelineLoading } = useQuery({
    queryKey: ['reports', 'pipeline'],
    queryFn: async () => {
      const res = await reportApi.getPipeline();
      return res.data.data;
    },
  });

  const { data: industryData, isLoading: industryLoading } = useQuery({
    queryKey: ['reports', 'industries'],
    queryFn: async () => {
      const res = await customerApi.getIndustries();
      const raw = (res.data.data || []) as { industry: string; count: number }[];
      // Top 9 + "Øvrige"
      const sorted = raw.sort((a, b) => b.count - a.count);
      const top = sorted.slice(0, 9);
      const rest = sorted.slice(9);
      const restCount = rest.reduce((sum, r) => sum + r.count, 0);
      if (restCount > 0) top.push({ industry: 'Øvrige', count: restCount });
      return { chart: top, all: sorted };
    },
  });

  const { data: performance, isLoading: performanceLoading } = useQuery({
    queryKey: ['reports', 'performance', period],
    queryFn: async () => {
      const res = await reportApi.getPerformance({ period });
      return res.data.data;
    },
    enabled: isAdmin,
  });

  if (dashboardLoading) {
    return (
      <Box>
        <PageHeader
          title="Rapporter & Analyse"
          subtitle="Henter rapport-data…"
          icon={<AssessmentIcon sx={{ fontSize: 18 }} />}
        />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={280} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={280} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const totalPipeline = pipeline?.reduce((sum: number, item: any) => sum + item._count._all, 0) || 0;

  return (
    <Box>
      <PageHeader
        title="Rapporter & Analyse"
        subtitle={`${dashboard?.totalCustomers || 0} kunder · ${dashboard?.activeCustomers || 0} aktive · ${totalPipeline} i pipeline`}
        icon={<AssessmentIcon sx={{ fontSize: 18 }} />}
        actions={
          <Box display="flex" gap={1} alignItems="center">
            {isAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    const res = await reportApi.exportOffersCsv();
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `tilbud-${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error('CSV export failed:', e);
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Eksporter tilbud (CSV)
              </Button>
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Periode</InputLabel>
              <Select
                value={period}
                label="Periode"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="week">Denne uge</MenuItem>
                <MenuItem value="month">Denne måned</MenuItem>
                <MenuItem value="quarter">Dette kvartal</MenuItem>
                <MenuItem value="year">Dette år</MenuItem>
              </Select>
            </FormControl>
          </Box>
        }
      />

      {/* KPI Cards */}
      <Grid container spacing={1.5} mb={3}>
        {[
          {
            label: 'Totale kunder',
            value: dashboard?.totalCustomers || 0,
            icon: <PeopleIcon sx={{ fontSize: 18 }} />,
            accent: '#208040',
            tint: '#EAF4ED',
          },
          {
            label: 'Aktive kunder',
            value: dashboard?.activeCustomers || 0,
            icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
            accent: '#16602F',
            tint: '#DDEEE2',
            sub: {
              text: `${dashboard?.conversionRate?.toFixed(1) || 0}% konvertering`,
              color: '#16602F',
              icon: <TrendingUpIcon sx={{ fontSize: 12 }} />,
            },
          },
          {
            label: 'Tabte kunder',
            value: dashboard?.lostCustomers || 0,
            icon: <CancelIcon sx={{ fontSize: 18 }} />,
            accent: '#C74A3E',
            tint: '#FBEAE8',
            sub: {
              text: `${dashboard?.lossRate?.toFixed(1) || 0}% tab`,
              color: '#A53A30',
              icon: <TrendingDownIcon sx={{ fontSize: 12 }} />,
            },
          },
          {
            label: 'Aktive leads',
            value: dashboard?.activeLeads || 0,
            icon: <ShippingIcon sx={{ fontSize: 18 }} />,
            accent: '#E8A33D',
            tint: '#FBF1DE',
          },
        ].map((kpi) => (
          <Grid key={kpi.label} size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  backgroundColor: kpi.accent,
                },
              }}
            >
              <CardContent sx={{ p: '12px 14px 12px 16px !important' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                        lineHeight: 1.2,
                      }}
                    >
                      {kpi.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        lineHeight: 1.1,
                        color: 'text.primary',
                        mt: 0.5,
                      }}
                    >
                      {kpi.value}
                    </Typography>
                    {kpi.sub && (
                      <Box display="flex" alignItems="center" gap={0.25} mt={0.5}>
                        <Box sx={{ color: kpi.sub.color, display: 'flex' }}>{kpi.sub.icon}</Box>
                        <Typography
                          sx={{ fontSize: '0.6875rem', fontWeight: 600, color: kpi.sub.color }}
                        >
                          {kpi.sub.text}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      bgcolor: kpi.tint,
                      color: kpi.accent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {kpi.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Pipeline Overview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Pipeline oversigt
              </Typography>

              {pipelineLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box>
                  {pipeline?.map((item: any) => {
                    const percentage = totalPipeline > 0
                      ? (item._count._all / totalPipeline) * 100
                      : 0;
                    return (
                      <Box key={item.status} mb={2}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2">
                            {statusLabels[item.status] || item.status}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {item._count._all}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: statusColors[item.status] || 'grey.500',
                              borderRadius: 1,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance by User — only visible for admins */}
        {isAdmin && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Medarbejder performance
              </Typography>

              {performanceLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Medarbejder</TableCell>
                        <TableCell align="right">Kunder</TableCell>
                        <TableCell align="right">Aktive</TableCell>
                        <TableCell align="right">Aktiviteter</TableCell>
                        <TableCell align="right">Konv. %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {performance?.userPerformance?.map((user: any) => (
                        <TableRow
                          key={user.userId}
                          hover
                          onClick={() => setSelectedUserId(user.userId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {user.firstName} {user.lastName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{user.totalCustomers}</TableCell>
                          <TableCell align="right">{user.activeCustomers}</TableCell>
                          <TableCell align="right">{user.totalActivities || 0}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${user.conversionRate?.toFixed(0) || 0}%`}
                              color={user.conversionRate > 50 ? 'success' : 'default'}
                              sx={{ borderRadius: 1 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!performance?.userPerformance || performance.userPerformance.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="text.secondary" py={2}>
                              Ingen data
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Per-user drill-down drawer */}
        <Drawer
          anchor="right"
          open={!!selectedUserId}
          onClose={() => setSelectedUserId(null)}
          PaperProps={{ sx: { width: fullScreenDrawer ? '100%' : 560, maxWidth: '100%' } }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" px={2.5} py={1.5}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {userDetail?.user
                  ? `${userDetail.user.firstName} ${userDetail.user.lastName}`
                  : performanceUserName(performance, selectedUserId)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Aktiviteter {periodLabels[period] || ''}
              </Typography>
            </Box>
            <IconButton onClick={() => setSelectedUserId(null)} aria-label="Luk" size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />

          {userDetailLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box px={2.5} py={2} sx={{ overflowY: 'auto' }}>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip size="small" color="primary" label={`${userDetail?.summary?.totalActivities ?? 0} aktiviteter`} sx={{ borderRadius: 1 }} />
                {Object.entries(userDetail?.summary?.activitiesByType || {}).map(([type, count]) => (
                  <Chip
                    key={type}
                    size="small"
                    variant="outlined"
                    label={`${activityTypeLabels[type] || type}: ${count as number}`}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
                <Chip size="small" variant="outlined" label={`Opgaver løst: ${userDetail?.summary?.tasksCompleted ?? 0}`} sx={{ borderRadius: 1 }} />
                <Chip size="small" variant="outlined" label={`Åbne opgaver: ${userDetail?.summary?.tasksOpen ?? 0}`} sx={{ borderRadius: 1 }} />
                <Chip size="small" variant="outlined" label={`Kunder: ${userDetail?.summary?.customersAssigned ?? 0}`} sx={{ borderRadius: 1 }} />
                <Chip size="small" variant="outlined" label={`Tilbud: ${userDetail?.summary?.offersCreated ?? 0}`} sx={{ borderRadius: 1 }} />
              </Box>

              {!userDetail?.activities || userDetail.activities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={3} textAlign="center">
                  Ingen aktiviteter i perioden
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Dato</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Aktivitet</TableCell>
                        <TableCell>Kunde</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userDetail.activities.map((a: any) => (
                        <TableRow key={a.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            <Typography variant="caption">
                              {format(new Date(a.date), 'd. MMM yyyy', { locale: da })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Chip size="small" label={activityTypeLabels[a.type] || a.type} sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" fontWeight={500}>{a.title}</Typography>
                            {a.outcome && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {a.outcome}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {a.customer ? (
                              <Link component={RouterLink} to={`/customers/${a.customer.id}`} underline="hover" variant="body2">
                                {a.customer.name}
                              </Link>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {userDetail?.activities?.length === 200 && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Viser de seneste 200 aktiviteter
                </Typography>
              )}
            </Box>
          )}
        </Drawer>

        {/* Recent Activity Summary */}
        <Grid size={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Aktivitetsoversigt
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={2}>
                    <Typography variant="h4" fontWeight={700} color="primary">
                      {dashboard?.activitiesThisWeek || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aktiviteter denne uge
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={2}>
                    <Typography variant="h4" fontWeight={700} color="info.main">
                      {dashboard?.callsThisWeek || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opkald denne uge
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={2}>
                    <Typography variant="h4" fontWeight={700} color="warning.main">
                      {dashboard?.meetingsThisWeek || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Møder denne uge
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={2}>
                    <Typography variant="h4" fontWeight={700} color="success.main">
                      {dashboard?.tasksCompleted || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opgaver fuldført
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Industry Distribution */}
        <Grid size={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Branchefordeling
              </Typography>

              {industryLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : industryData?.chart && industryData.chart.length > 0 ? (
                <Grid container spacing={3}>
                  {/* Donut Chart */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={industryData.chart}
                          dataKey="count"
                          nameKey="industry"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={120}
                          paddingAngle={2}
                        >
                          {industryData.chart.map((_: any, i: number) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => [value.toLocaleString('da-DK'), 'Kunder']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Legend */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                      {industryData.chart.map((item: any, i: number) => (
                        <Box key={item.industry} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{item.industry}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* Horizontal Bar Chart */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={industryData.all}
                        layout="vertical"
                        margin={{ left: 120, right: 20, top: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="industry"
                          width={115}
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip
                          formatter={(value: number) => [value.toLocaleString('da-DK'), 'Kunder']}
                        />
                        <Bar dataKey="count" fill="#208040" radius={[0, 4, 4, 0]} barSize={14} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  Ingen branchedata
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
